/**
 * Environments: the interfaces plugins bring, one per use (Coding, Writing, UI/UX...). A
 * plugin declares one in its manifest (`environment "Writing" { views ...; windows ... }`); the
 * environment bar lists those of the plugins turned on in the project, so a new environment
 * plugin shows up there by itself once it is on. A project may have several, and each person
 * works in the one they pick (remembered in the browser): one may write while another codes.
 *
 * Environments are spaces inside the project: each has its own nodes, groups and kinds (the
 * graph mirror shows the current one's part, the kinds service offers its kinds). An environment
 * may hold several workspaces, boards sharing nothing (Coding: the app, and a game beside it);
 * each page works in one of them, chosen per environment and remembered in the browser.
 *
 * An environment chooses the views of the graph shown (the view plugins it names, among those
 * on) and the windows of its first layout; the window manager then keeps each environment's
 * own arrangement (`app/environmentLayouts.ts`: the window manager is above this layer).
 */
import { createEffect, createMemo, createSignal, on, type Accessor } from "solid-js";
import type { EnvironmentDef } from "@protocol/EnvironmentDef";
import type { PluginInfo } from "@protocol/PluginInfo";
import type { Workspace } from "@protocol/Workspace";
import type { CommandSet } from "../shared/commands";
import type { MenuSection } from "../shared/menus";
import { load, save } from "../shared/storage";
import type { GraphMirror } from "../client/graph";
import { isReady, type Plugins } from "./plugins";
import type { View } from "./view";

/** Where the chosen environment is kept in the browser. */
const KEY = "knodal-environment";

/** Where the workspace chosen in each environment is kept in the browser (environment id → workspace id). */
const WORKSPACES_KEY = "knodal-workspaces";

/** An environment of a plugin that is on: its plugin's id and what it declares. */
export interface Environment extends EnvironmentDef {
  /** The id of the plugin bringing it (also the environment's id). */
  id: string;
}

/** The environments of the plugins in use (on, approved, working, with what they require), in the plugin list's order. */
export function environmentsOf(plugins: readonly PluginInfo[]): Environment[] {
  return plugins.filter((p) => p.environment && isReady(p)).map((p) => ({ ...p.environment!, id: p.id }));
}

/** The view plugins in use, in the plugin list's order. */
export function viewsOn(plugins: readonly PluginInfo[]): string[] {
  return plugins.filter((p) => p.view && isReady(p)).map((p) => p.id);
}

/** The views `environment` shows: the ones it names (in its order) that are on; it names none, or there is no environment: every view on. */
export function viewsOf(environment: Environment | null, on: readonly string[]): string[] {
  if (!environment?.views.length) return [...on];
  return environment.views.filter((v) => on.includes(v));
}

/** The environment worked in: the chosen one while it is there, else the first. */
export function currentOf(list: readonly Environment[], chosen: string | null): Environment | null {
  return list.find((e) => e.id === chosen) ?? list[0] ?? null;
}

/** The workspaces of `environment` in the project, in order (none: the environment is one board). */
export function workspacesOf(all: readonly Workspace[] | undefined, environment: string | null): Workspace[] {
  return environment ? (all ?? []).filter((w) => w.environment === environment) : [];
}

/** The workspace worked in among `list`: the chosen one while it is there, else the first (`null`: none listed). */
export function currentWorkspaceOf(list: readonly Workspace[], chosen: string | null | undefined): Workspace | null {
  return list.find((w) => w.id === chosen) ?? list[0] ?? null;
}

/**
 * Whether a button opening window `window` (of Window menu group `group`) belongs in
 * `environment`: always, unless the window is of {@link ENVIRONMENT_GROUPS} and the environment
 * lists windows but not this one (Analyze in Writing).
 */
export function windowOffered(environment: Environment | null, window: string, group: string | undefined): boolean {
  return !environment?.windows.length || !ENVIRONMENT_GROUPS.includes(group ?? "") || environment.windows.includes(window);
}

/** The groups of windows (Window menu) an environment chooses among; the app's own (plugins, log, spaces...) stay as they are. */
export const ENVIRONMENT_GROUPS: readonly string[] = ["Graph", "Project"];

/** The environments service. */
export interface Environments {
  /** The environments of the plugins on in the project. */
  list: Accessor<Environment[]>;
  /** The one worked in (`null`: none is on). */
  current: Accessor<Environment | null>;
  /** Works in environment `id` (kept for next time, even while it is off). */
  choose(id: string): void;
  /** The workspaces of the environment worked in (none: it is one board). */
  workspaces: Accessor<Workspace[]>;
  /** The workspace worked in (`null`: the environment is one board). */
  workspace: Accessor<Workspace | null>;
  /** Works in workspace `id` of the current environment (kept for next time). */
  chooseWorkspace(id: string): void;
}

/** What the service is built from. */
export interface EnvironmentsDeps {
  /** The plugins and which are on. */
  plugins: Plugins;
  /** The graph mirror, told which environment's part to show, and read for its workspaces. */
  graph: Pick<GraphMirror, "setScope" | "full">;
  /** The view service, told which views exist here. */
  view: View;
}

/** Starts the environments service. */
export function createEnvironments({ plugins, graph, view }: EnvironmentsDeps): Environments {
  const [chosen, setChosen] = createSignal<string | null>(load<string | null>(KEY, null));
  const list = createMemo(() => environmentsOf(plugins.list()), [], { equals: (a, b) => JSON.stringify(a) === JSON.stringify(b) });
  const current = createMemo(() => currentOf(list(), chosen()));
  const [chosenWorkspaces, setChosenWorkspaces] = createSignal<Record<string, string>>(load<Record<string, string>>(WORKSPACES_KEY, {}));
  const workspaces = createMemo(() => workspacesOf(graph.full().workspaces, current()?.id ?? null), [], { equals: (a, b) => JSON.stringify(a) === JSON.stringify(b) });
  const workspace = createMemo(() => currentWorkspaceOf(workspaces(), chosenWorkspaces()[current()?.id ?? ""]), null, { equals: (a, b) => a?.id === b?.id && a?.name === b?.name });
  // Environments (and workspaces) share nothing: the graph shows the current board only.
  createEffect(
    on([current, () => workspace()?.id ?? null], ([environment, board], before) => {
      graph.setScope(environment ? { id: environment.id, first: list()[0]?.id === environment.id, workspace: board } : null);
      // What was selected is another board's now; and the new one is fitted in view, once its
      // own layout is on screen (the graph window may have changed size: two frames later).
      // Workspaces coming or going with a whole new graph (opening, loading) fit nothing: only
      // moving from one workspace to another does.
      const moved = board !== before?.[1] && board !== null && before?.[1] !== null;
      if (before !== undefined && (environment?.id !== before[0]?.id || moved)) {
        view.selectNode(null);
        requestAnimationFrame(() => requestAnimationFrame(() => view.requestFit()));
      }
    }),
  );
  // Before the plugin list arrives every view stays offered, so nothing jumps at start.
  createEffect(() => {
    if (plugins.list().length) view.setViews(viewsOf(current(), viewsOn(plugins.list())));
  });
  return {
    list,
    current,
    choose(id) {
      setChosen(id);
      save(KEY, id);
    },
    workspaces,
    workspace,
    chooseWorkspace(id) {
      const environment = current()?.id;
      if (!environment) return;
      const next = { ...chosenWorkspaces(), [environment]: id };
      setChosenWorkspaces(next);
      save(WORKSPACES_KEY, next);
    },
  };
}

/** The commands `environment.use <id>` and `workspace.use <id>` (for shortcuts in the config). */
export function environmentCommands(environments: Environments): CommandSet {
  return {
    "environment.use": (id) => id && environments.choose(id),
    "workspace.use": (id) => id && environments.chooseWorkspace(id),
  };
}

/** The View menu's environments: one line each, the current one checked. */
export function environmentMenus(environments: Environments): MenuSection[] {
  return [
    {
      menu: "view",
      section: "Environment",
      order: 5,
      entries: () => environments.list().map((e) => ({ label: e.label, command: "environment.use", argument: e.id, checked: environments.current()?.id === e.id })),
    },
    {
      menu: "view",
      section: "Workspace",
      order: 6,
      entries: () => environments.workspaces().map((w) => ({ label: w.name, command: "workspace.use", argument: w.id, checked: environments.workspace()?.id === w.id })),
    },
  ];
}
